import { FC, useState, useEffect } from "react";
import { Box, Typography, FormControl, Select, MenuItem, Chip } from "@mui/material";
import { fromHourValues } from "./fromHourValues";
import { toHourValues } from "./toHourValues";
import { WeekDay } from "./weekDays";

// Define types for your props
interface WeeklyChargerReservationProps {
    readOnly: boolean;
    dayOfWeekLabel: string;
    dayInfo: {
        dayIndex: number;
        fromHour: string;
        toHour: string;
        day: string;
    };
    onTimeChange: (dayIndex: number, type: keyof WeekDay, value: string) => void;
}

const WeeklyChargerReservation: FC<WeeklyChargerReservationProps> = ({ readOnly, dayOfWeekLabel, dayInfo, onTimeChange }) => {
    const [fromHour, setFromHour] = useState(dayInfo.fromHour);
    const [toHour, setToHour] = useState(dayInfo.toHour);

    // Update local state when dayInfo changes
    useEffect(() => {
        setFromHour(dayInfo.fromHour);
        setToHour(dayInfo.toHour);
    }, [dayInfo.fromHour, dayInfo.toHour]);

    const handleFromHourChange = (value: string) => {
        setFromHour(value);
        onTimeChange(dayInfo.dayIndex, 'fromHour', value);
    };

    const handleToHourChange = (value: string) => {
        setToHour(value);
        onTimeChange(dayInfo.dayIndex, 'toHour', value);
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight="medium">
                    {dayOfWeekLabel}
                </Typography>
                {readOnly && (
                    <Chip 
                        label={`${fromHour} - ${toHour}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                    />
                )}
            </Box>
            
            {!readOnly && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                            value={fromHour}
                            onChange={(e) => handleFromHourChange(e.target.value)}
                            displayEmpty
                        >
                            {fromHourValues.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <Typography variant="body2" color="text.secondary">
                        to
                    </Typography>
                    
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                            value={toHour}
                            onChange={(e) => handleToHourChange(e.target.value)}
                            displayEmpty
                        >
                            {toHourValues.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}
        </Box>
    );
}

export default WeeklyChargerReservation;
